import { Field as BaseField } from '@base-ui/react/field';
import type * as React from 'react';
import { cn } from '../../lib/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
  ref?: React.Ref<HTMLTextAreaElement>;
  onValueChange?: React.ComponentProps<typeof BaseField.Control>['onValueChange'];
}

// SAFETY: BaseField.Control is typed for <input>, but `render` swaps in a <textarea>. Base UI
// merges its computed props (id, aria-*, data-*) and the render element's ref onto whatever
// `render` provides, so no <input> is ever rendered. The two attribute sets overlap too little for
// a direct assertion, hence the widening step.
// oxlint-disable-next-line typescript/no-unsafe-type-assertion, anti-slop/no-chained-type-assertions -- see above; Field.Control exposes no element generic, so tsc rejects the single-step assertion
const TextareaControl = BaseField.Control as unknown as React.FC<
  TextareaProps & { render: React.ReactElement }
>;

export function Textarea({ className, ref, ...props }: TextareaProps) {
  return (
    <TextareaControl
      ref={ref}
      // Base UI Field.Control merges the label association onto this element at runtime.
      // oxlint-disable-next-line jsx-a11y/control-has-associated-label
      render={<textarea />}
      className={cn(
        'text-foreground w-full rounded-md border bg-white',
        'px-3.5 py-2.5 text-base',
        'border-border',
        'min-h-20',
        // Focus — match Input focus ring
        'focus:outline-2 focus:-outline-offset-1 focus:outline-solid',
        'focus:outline-ring',
        'placeholder:text-muted-foreground',
        // data-invalid comes from the Base UI Field context.
        'data-invalid:border-destructive',
        'data-invalid:focus:outline-destructive',
        // aria-invalid covers standalone use.
        'aria-invalid:border-destructive',
        'aria-invalid:focus:outline-destructive',
        'data-disabled:border-border-disabled data-disabled:bg-disabled data-disabled:text-text-disabled data-disabled:cursor-not-allowed',
        'disabled:border-border-disabled disabled:bg-disabled disabled:text-text-disabled disabled:cursor-not-allowed',
        'transition-colors',
        className,
      )}
      {...props}
    />
  );
}
